import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { updateCanonicalTag, getProductionUrlForPageId } from "@/lib/canonical-utils";
import { updateMetaTag } from "@/lib/seo-utils";

/**
 * Custom hook to dynamically update page SEO metadata
 * Fetches SEO data from the database and updates document title, meta description,
 * Open Graph tags, Twitter Card tags, and canonical URL
 * 
 * @param pageId - The unique identifier for the page in the seo table (e.g., "home", "thank-you", "terms", "privacy")
 */
export const useSEO = (pageId: string) => {
  useEffect(() => {
    const fetchAndApplySEO = async () => {
      try {
        const { data, error } = await supabase
          .from("seo")
          .select("*")
          .eq("id", pageId)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching SEO data:", error);
          return;
        }

        if (!data) {
          console.warn(`No SEO data found for page: ${pageId}`);
          return;
        }

        // Update document title
        document.title = data.title;

        // Update or create meta description
        updateMetaTag("name", "description", data.meta_description);

        // Canonical URL
        const canonicalUrl = getProductionUrlForPageId(pageId);
        updateCanonicalTag(canonicalUrl);

        // Update Open Graph tags
        updateMetaTag("property", "og:title", data.title);
        updateMetaTag("property", "og:description", data.meta_description);
        updateMetaTag("property", "og:url", canonicalUrl);
        if (data.social_image_url) {
          updateMetaTag("property", "og:image", data.social_image_url);
        }
        updateMetaTag("property", "og:type", "website");

        // Update Twitter Card tags (no twitter:site — company has no Twitter account)
        updateMetaTag("name", "twitter:card", data.twitter_card_type || "summary_large_image");
        updateMetaTag("name", "twitter:title", data.title);
        updateMetaTag("name", "twitter:description", data.meta_description);
        if (data.social_image_url) {
          updateMetaTag("name", "twitter:image", data.social_image_url);
        }
      } catch (error) {
        console.error("Error in useSEO hook:", error);
      }
    };

    fetchAndApplySEO();
  }, [pageId]);
};
